import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatableApp } from '@vendasta/marketplace-packages';
import {
  ListSubstitutionParametersRequestFiltersInterface,
  SubstitutionParameter,
  TemplatesService,
} from '@vendasta/templates';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  AppListingServiceInterface,
  EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN,
} from '@galaxy/email-ui/email-builder/src/services/app-listing-service.interface';

export interface Option {
  title: string;
  value: string;
}

export interface VariableMenuItemOptions {
  requiredParams: string[];
  nestedDynamicSelector: boolean;
}

export interface VariableMenuItem {
  textKey: string;
  replacementTags: string[];
  template?: string;
  subItems?: SubstitutionParameter[];
  options?: VariableMenuItemOptions;
}

const defaultDynamicComponents = [
  {
    textKey: 'DYNAMIC_COMPONENT_SELECTOR.CONTACT_DETAILS',
    replacementTags: ['crmContactFirstName', 'crmContactLastName'],
  },
];

@Injectable()
export class DynamicComponentSelectorService {
  private parentVariableCategories: VariableMenuItem[] = [];

  constructor(
    private readonly templatesService: TemplatesService,
    @Inject(EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN) @Optional() private appListingService: AppListingServiceInterface,
  ) {
    this.overrideParentVariableCategories(defaultDynamicComponents);
  }

  public hasComponentsToDisplay(): boolean {
    return this.parentVariableCategories.length > 0;
  }

  public getVariables(filters: ListSubstitutionParametersRequestFiltersInterface): Observable<VariableMenuItem[]> {
    return this.templatesService
      .listSubstitutionParameters(filters)
      .pipe(map((params) => this.filterSubstitutionParameters(params)));
  }

  private filterSubstitutionParameters(params: SubstitutionParameter[]): VariableMenuItem[] {
    return this.parentVariableCategories.map((v) => ({
      textKey: v.textKey,
      replacementTags: v.replacementTags,
      options: v.options,
      subItems: params
        .filter((p) => v.replacementTags.some((t) => t === p.replacementTag))
        .sort(function (a, b): number {
          return v.replacementTags.indexOf(a.replacementTag) - v.replacementTags.indexOf(b.replacementTag);
        }),
    }));
  }

  public getProductSubstitutionVariables(): Observable<ActivatableApp[]> {
    return this.appListingService.listAllActivatableApps().pipe(take(1));
  }

  public overrideParentVariableCategories(variableCategories: VariableMenuItem[]): VariableMenuItem[] {
    return (this.parentVariableCategories = variableCategories);
  }
}
